import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../../../axiosinterceptor";

const GET_ALL_ARCHIVED_RULES_URL = "/get_all_archieve_rules";
const FETCH_ARCHIVED_PATHWAY_ENTRY_POINTS_URL =
  "/fetch_archieve_pathway_entry_points";
const FETCH_ARCHIVED_BY_USERS_URL = "/fetch_archived_by_users";

const PATHWAY_OPTIONS_PAGE_SIZE = 10;
const ARCHIVED_BY_OPTIONS_PAGE_SIZE = 10;

// Key used to persist the currently selected archived rule's id across a
// page refresh on the view/edit-archived-rule pages.
const SELECTED_RULE_ID_KEY = "selectedArchivedRuleId";
const formatDateForApi = (date) => {
  if (!date) return "";

  const [year, month, day] = date.split("-");
  return `${day}-${month}-${year}`;
};
export const fetchArchivedRules = createAsyncThunk(
  "archivedRules/fetchArchivedRules",
  async (_, { getState, rejectWithValue }) => {
    try {
      const { archiveRule } = getState();
      const { filters, pageNumber, pageSize } = archiveRule;

      const payload = {
        rule_id: filters.rule_id,
        description: filters.description,
        trigger_code: filters.trigger_code,
        status: filters.status,
        pathway_entry: filters.pathway_entry,
        archived_by: filters.archived_by,
        page_number: pageNumber,
        page_size: pageSize,
        sort_by: filters.sort_by,
        sort_order: filters.sort_order,
        archieved_date: formatDateForApi(filters.archieved_date),
      };

      const response = await axiosInstance.post(
        GET_ALL_ARCHIVED_RULES_URL,
        payload
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch archived rules"
      );
    }
  }
);

export const fetchPathwayEntryOptions = createAsyncThunk(
  "archivedRules/fetchPathwayEntryOptions",
  async (_, { rejectWithValue }) => {
    try {
      let offset = 0;
      let hasMore = true;
      let allOptions = [];

      while (hasMore) {
        const response = await axiosInstance.get(
          FETCH_ARCHIVED_PATHWAY_ENTRY_POINTS_URL,
          { params: { page_size: PATHWAY_OPTIONS_PAGE_SIZE, offset } }
        );
        allOptions = allOptions.concat(response.data?.data || []);
        hasMore = Boolean(response.data?.has_more);
        offset += PATHWAY_OPTIONS_PAGE_SIZE;
      }

      return allOptions;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch pathway entry points"
      );
    }
  }
);

export const fetchArchivedByOptions = createAsyncThunk(
  "archivedRules/fetchArchivedByOptions",
  async (_, { rejectWithValue }) => {
    try {
      let offset =0;
      let hasMore = true;
      let allOptions = [];

      while (hasMore) {
        const response = await axiosInstance.get(FETCH_ARCHIVED_BY_USERS_URL, {
          params: { page_size: ARCHIVED_BY_OPTIONS_PAGE_SIZE, offset },
        });
        allOptions = allOptions.concat(response.data?.data || []);
        hasMore = Boolean(response.data?.has_more);
        offset += ARCHIVED_BY_OPTIONS_PAGE_SIZE;
      }

      return allOptions;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch archived-by users"
      );
    }
  }
);

const initialFilters = {
  rule_id: [],
  description: [],
  trigger_code: [],
  status: [],
  pathway_entry: [],
  archived_by: [],
  sort_by: [],
  sort_order: [],
  archieved_date: ""
};

const initialState = {
  rules: [],
  cards: {
    total_archieved_rules: 0,
    archieved_this_month: 0,
    last_7days_archieved: 0,
  },
  pageNumber: 1,
  pageSize: 10,
  totalCount: 0,
  rulesLoading: false,
  rulesError: null,

  pathwayOptions: [],
  pathwayOptionsLoading: false,
  pathwayOptionsError: null,

  archivedByOptions: [],
  archivedByOptionsLoading: false,
  archivedByOptionsError: null,

  filters: initialFilters,

  selectedRuleId: sessionStorage.getItem(SELECTED_RULE_ID_KEY) || null,
};

const archivedRulesSlice = createSlice({
  name: "archivedRules",
  initialState,
  reducers: {
    setRuleIdFilter(state, action) {
      state.filters.rule_id = action.payload ? [action.payload] : [];
      state.pageNumber = 1;
    },
    setDescriptionFilter(state, action) {
      state.filters.description = action.payload ? [action.payload] : [];
      state.pageNumber = 1;
    },
    setPathwayEntryFilter(state, action) {
      state.filters.pathway_entry = action.payload ? [action.payload] : [];
      state.pageNumber = 1;
    },
    setStatusFilter(state, action) {
      state.filters.status = action.payload ? [action.payload] : [];
      state.pageNumber = 1;
    },
    setTriggerCodeFilter(state, action) {
     state.filters.trigger_code = Array.isArray(action.payload)
     ? action.payload
     : action.payload
     ? [action.payload]
     : [];
  state.pageNumber = 1;
    },
    setSort(state, action) {
      const { sortBy, sortOrder } = action.payload || {};
      state.filters.sort_by = sortBy ? [sortBy] : [];
      state.filters.sort_order = sortOrder ? [sortOrder] : [];
    },
    setPageNumber(state, action) {
      state.pageNumber = action.payload;
    },
    setSelectedRuleId(state, action) {
      state.selectedRuleId = action.payload;
      sessionStorage.setItem(SELECTED_RULE_ID_KEY, action.payload);
    },
    setArchivedDateFilter(state, action) {
     state.filters.archieved_date = action.payload || "";
      state.pageNumber = 1;
    },
    setArchivedByFilter(state, action) {
      state.filters.archived_by = action.payload ? [action.payload] : [];
      state.pageNumber = 1;
    },
    resetArchivedRulesFilters(state) {
      state.filters = initialFilters;
      state.pageNumber = 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchArchivedRules.pending, (state) => {
        state.rulesLoading = true;
        state.rulesError = null;
      })
      .addCase(fetchArchivedRules.fulfilled, (state, action) => {
        state.rulesLoading = false;
        state.rules = action.payload?.data || [];
        state.cards = action.payload?.cards || state.cards;
        state.pageNumber = action.payload?.page_number || state.pageNumber;
        state.pageSize = action.payload?.page_size || state.pageSize;
        state.totalCount = action.payload?.total_count || 0;
      })
      .addCase(fetchArchivedRules.rejected, (state, action) => {
        state.rulesLoading = false;
        state.rulesError = action.payload || action.error.message;
      })

      .addCase(fetchPathwayEntryOptions.pending, (state) => {
        state.pathwayOptionsLoading = true;
        state.pathwayOptionsError = null;
      })
      .addCase(fetchPathwayEntryOptions.fulfilled, (state, action) => {
        state.pathwayOptionsLoading = false;
        state.pathwayOptions = action.payload;
      })
      .addCase(fetchPathwayEntryOptions.rejected, (state, action) => {
        state.pathwayOptionsLoading = false;
        state.pathwayOptionsError = action.payload || action.error.message;
      })

      .addCase(fetchArchivedByOptions.pending, (state) => {
        state.archivedByOptionsLoading = true;
        state.archivedByOptionsError = null;
      })
      .addCase(fetchArchivedByOptions.fulfilled, (state, action) => {
        state.archivedByOptionsLoading = false;
        state.archivedByOptions = action.payload;
      })
      .addCase(fetchArchivedByOptions.rejected, (state, action) => {
        state.archivedByOptionsLoading = false;
        state.archivedByOptionsError = action.payload || action.error.message;
      });
  },
});

export const {
  setRuleIdFilter,
  setDescriptionFilter,
  setPathwayEntryFilter,
  setStatusFilter,
  setTriggerCodeFilter,
  setSort,
  setPageNumber,
  setSelectedRuleId,
  setArchivedDateFilter,
  setArchivedByFilter,
  resetArchivedRulesFilters,
} = archivedRulesSlice.actions;

export default archivedRulesSlice.reducer;